import React, { ReactNode } from 'react';

import PropTypes from 'prop-types';

import { variantSwitch } from '@adsk/alloy-react-helpers';
import theme, {
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';
import Tooltip from '@adsk/alloy-react-tooltip';

import { PERMISSION_INDICATOR_PILL_TYPES } from '../consts';
import { pillValuePropType } from '../helpers/propTypes';
import { PillValue } from '../types';

type PermissionIndicatorProps = StylableComponent<HTMLDivElement> & {
  pillCount?: number;
  label?: ReactNode;
  pillValue?: PillValue;
  pillClassName?: { [key: number]: string };
  tooltip?: ReactNode;
  disabled?: boolean;
};

const PermissionIndicator = React.forwardRef<
  HTMLDivElement,
  PermissionIndicatorProps
>(
  (
    {
      pillCount = 3,
      label,
      pillValue = {},
      pillClassName = {},
      tooltip,
      disabled,
      style,
      ...rest
    },
    ref,
  ) => {
    const content = (
      <div
        css={[
          {
            height: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
          },
        ]}
      >
        {new Array(pillCount).fill(1).map((_, index) => (
          <div
            key={index}
            className={pillClassName[index]}
            css={[
              {
                width: '24px',
                height: '8px',
                marginRight: theme.spacing.xxSmall,
                backgroundColor: theme.colors.charcoal100,
                transition: 'background-color 0.2s linear',
                borderRadius: theme.borderRadius.xxLarge,
                flex: 'none',
              },
              variantSwitch({
                statement:
                  pillValue[index] || PERMISSION_INDICATOR_PILL_TYPES.EMPTY,
                variants: {
                  [PERMISSION_INDICATOR_PILL_TYPES.FULL]: {
                    backgroundColor: disabled
                      ? theme.colors.adskBlue100
                      : theme.colors.adskBlue500,
                  },
                  [PERMISSION_INDICATOR_PILL_TYPES.SEMI]: {
                    border: `1px solid ${
                      disabled
                        ? theme.colors.adskBlue100
                        : theme.colors.adskBlue500
                    }`,
                  },
                },
              }),
            ]}
          />
        ))}
      </div>
    );

    return (
      <div
        ref={ref}
        css={[
          {
            overflow: 'hidden',
          },
          style,
        ]}
        {...rest}
      >
        {tooltip ? <Tooltip content={tooltip}>{content}</Tooltip> : content}
        {!!label && (
          <div
            css={[
              theme.typography.bodySmall,
              {
                color: theme.colors.charcoal900,
                marginTop: '3px',
                textAlign: 'center',
              },
              theme.typography.ellipsis,
            ]}
          >
            {label}
          </div>
        )}
      </div>
    );
  },
);

PermissionIndicator.displayName = 'PermissionIndicator';

PermissionIndicator.propTypes = {
  /**
   * Class name for the component
   */
  className: PropTypes.string,
  /**
   * style for the component
   */
  style: stylePropType,
  /**
   * Number of pills to show
   */
  pillCount: PropTypes.number,
  /**
   * Label to appear above the pills
   */
  label: PropTypes.node,
  /**
   * A tooltip show when hover
   */
  tooltip: PropTypes.node,
  /**
   * Whether the component is disabled
   */
  disabled: PropTypes.bool,
  /**
   * pill value to render a specific pill inside PermissionIndicator.
   *
   * Use Object for new permission like { 0: PILL_TYPES.SEMI, 1: PILL_TYPES.FULL }.
   */
  pillValue: pillValuePropType,
  /**
   * pill class name associated to specific pill.
   *
   * Like { 0: 'PermissionIndicator_pill--semi', 1: 'PermissionIndicator_pill--full' }.
   */
  pillClassName: PropTypes.objectOf(PropTypes.string.isRequired),
};

export default Object.assign(PermissionIndicator, {
  PILL_TYPES: PERMISSION_INDICATOR_PILL_TYPES,
});
